import React, { useEffect, useRef } from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { useHotelSearchParams } from './useHotelSearchParams';
import { config } from '@static/js/env.config';
import { useHotelBookingRedirectHealth, wrapWithHotelBookingRedirectProvider } from '../../HotelBooking.health';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { loadable } from '@shared/core/loadableFromNamedExport';
import { useEventDisplayName } from '@shared/utils/eventInfo/useEventDisplayName';
import { useHotelSearchConfig } from './useHotelSearchConfig';
import { useQueryParamHelper } from '@shared/core/queryString';
import { HotelSearchContextParams } from '@shared/constants/accommodations';

const AccommodationsConsoleConfigProvider = loadable(
  () => import(/* webpackChunkName: "content/accommodations/config-provider" */ '@apps/accommodations/AccommodationsConsoleConfigContext'),
  'AccommodationsConsoleConfigProvider',
);

const AccommodationsConsoleDialog = loadable(
  () => import(/* webpackChunkName: "content/accommodations/console-dialog" */ '@apps/accommodations/AccommodationsConsole'),
  'AccommodationsConsoleDialog',
);

export const HotelSearch = wrapWithHotelBookingRedirectProvider(({ eventHandle }: { eventHandle: string }) => {
  const redirectInProgress = useRef(false);
  const { onLoadFailure, onLoadComplete } = useHotelBookingRedirectHealth();
  const telemetry = useHotelBookingGuestSiteTelemetry();
  const eventDisplayName = useEventDisplayName();
  const queryParamHelper = useQueryParamHelper();

  const [isJoyHotelSearchPageEnabledFlag, isJoyHotelSearchPageLoading] = useIsFeatureEnabled('joyHotelSearchPage');
  const [isTravelAccommodationsConsoleV2Enabled, isTravelAccommodationsConsoleV2Loading] = useIsFeatureEnabled('travelAccommodationsConsoleV2');
  const { loading, buildRedirectUrl, ...restParams } = useHotelSearchParams(eventHandle);

  const { config: consoleConfig } = useHotelSearchConfig();

  const selectedHotelId = queryParamHelper.getValueString(HotelSearchContextParams.selectedHotel) || undefined;

  const isJoyHotelSearchPageEnabled = isJoyHotelSearchPageEnabledFlag || isTravelAccommodationsConsoleV2Enabled;
  const isFeatureFlagLoading = isJoyHotelSearchPageLoading || isTravelAccommodationsConsoleV2Loading;

  useMount(() => {
    telemetry.hotelSearch.enter();
  });

  // Call onLoadComplete when showing AccommodationsConsole to prevent timeout
  useEffect(() => {
    if (!isFeatureFlagLoading && isJoyHotelSearchPageEnabled) {
      onLoadComplete();
    }
  }, [isFeatureFlagLoading, isJoyHotelSearchPageEnabled, onLoadComplete]);

  // Old redirect logic (control)
  useEffect(() => {
    if (isFeatureFlagLoading || isJoyHotelSearchPageEnabled || redirectInProgress.current || loading) {
      return;
    }

    try {
      /**
       * Try redirect to Nuitee
       */
      const nuiteeRedirectUrl = buildRedirectUrl({
        url: config.nuiteeWhitelableBaseUrl,
        fields: ['clientReference', 'placeId', 'name', 'checkin', 'checkout', 'currency', 'language', 'xData'],
      });

      telemetry.partnerHotelSearchRedirect({
        destinationName: 'stays',
        destinationUrl: nuiteeRedirectUrl,
        clientReference: restParams.clientReference,
        locationPlaceId: restParams.placeId,
        locationName: restParams.name,
        locationSource: restParams.locationSource,
        checkin: restParams.checkin,
        checkout: restParams.checkout,
        utm_source: restParams.utm_source,
        utm_source_secondary: restParams.utm_source_secondary,
        utm_medium: restParams.utm_medium,
        utm_campaign: restParams.utm_campaign,
        onTelemetrySend: () => {
          onLoadComplete({ url: nuiteeRedirectUrl, isExternalUrl: true });
        },
      });

      redirectInProgress.current = true;
    } catch (error) {
      onLoadFailure(`HotelSearch: Failed to build redirect URL`, error as Error, { url: config.nuiteeWhitelableBaseUrl, isExternalUrl: true });
      redirectInProgress.current = true;
    }
  }, [buildRedirectUrl, loading, onLoadComplete, onLoadFailure, restParams, telemetry, isFeatureFlagLoading, isJoyHotelSearchPageEnabled]);

  // Show loader while feature flag is loading
  if (isFeatureFlagLoading) {
    return <JoyLogoLoader loaderKey="hotel-search" />;
  }

  // New AccommodationsConsole implementation (treatment)
  if (isJoyHotelSearchPageEnabled) {
    return (
      <AccommodationsConsoleConfigProvider config={consoleConfig}>
        <AccommodationsConsoleDialog
          isOpen={true}
          eventHandle={eventHandle}
          eventName={eventDisplayName}
          selectedHotelId={selectedHotelId}
        />
      </AccommodationsConsoleConfigProvider>
    );
  }

  // Old redirect logic (control) - show loader until redirect starts
  return <>{!redirectInProgress.current && <JoyLogoLoader loaderKey="hotel-search" />}</>;
});
